import React from "react";
import { interpolate } from "remotion";

export interface SpecularShineProps {
  progress: number; // 0 to 1
  angle?: number; // degrees, default 28
  coreWidth?: number; // width of intense core beam in px, default 90
  haloWidth?: number; // width of soft outer halo in px, default 260
  color?: string; // default "255, 255, 255"
  coreIntensity?: number; // 0 to 1, default 0.95
  haloIntensity?: number; // 0 to 1, default 0.35
  borderRadius?: number;
}

/**
 * Authentic Specular Shine Element adapted from Remotion Elements (commerce/shine)
 * Projects an optical diagonal glass glare sweep across any card or banner.
 */
export const SpecularShine: React.FC<SpecularShineProps> = ({
  progress,
  angle = 28,
  coreWidth = 90,
  haloWidth = 260,
  color = "255, 255, 255",
  coreIntensity = 0.95,
  haloIntensity = 0.35,
  borderRadius,
}) => {
  // Map progress (0 to 1) across the sweep range (-120% to +220%)
  const leftPos = interpolate(progress, [0, 1], [-120, 220]);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        borderRadius,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 30,
      }}
    >
      {/* Outer Soft Halo Glow */}
      <div
        style={{
          position: "absolute",
          top: "-50%",
          bottom: "-50%",
          width: haloWidth,
          left: `${leftPos}%`,
          background: `linear-gradient(90deg, transparent 0%, rgba(${color}, ${haloIntensity}) 50%, transparent 100%)`,
          transform: `skewX(-${angle}deg)`,
          filter: "blur(18px)",
          mixBlendMode: "screen",
        }}
      />

      {/* Inner Piercing Core Specular Beam */}
      <div
        style={{
          position: "absolute",
          top: "-50%",
          bottom: "-50%",
          width: coreWidth,
          left: `calc(${leftPos}% + ${(haloWidth - coreWidth) / 2}px)`,
          background: `linear-gradient(90deg, transparent 0%, rgba(${color}, ${coreIntensity}) 50%, transparent 100%)`,
          transform: `skewX(-${angle}deg)`,
          mixBlendMode: "screen",
        }}
      />
    </div>
  );
};
